import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { CommonActions } from './common.actions';
import { CommonService } from '../../services/common/common.service';
import { siteConfig } from 'app/siteSettings';

export const loadMasterData$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadMasterData),
            switchMap(() =>
                commonService.getMasterDataAll().pipe(
                    map((res: any) => {
                        const masterData: Record<string, any[]> = {};
                        const cats = siteConfig.masterDataCategories;
                        if (res.code === 200 && res.data?.length > 0) {
                            Object.keys(cats).forEach(key => {
                                masterData[key] = res.data.filter((item: any) => item.mas_cat_id === cats[key]);
                            });
                        }
                        return CommonActions.loadMasterDataSuccess({ masterData });
                    }),
                    catchError(err => of(CommonActions.loadMasterDataFailure({
                        error: err?.error?.message ?? 'Failed to load master data'
                    })))
                )
            )
        ),
    { functional: true }
);

export const loadBusinessEntities$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadBusinessEntities),
            switchMap(() => {
                const keys = Object.keys(siteConfig.businessEntitiesTypes);
                const obs = keys.map(key => commonService.getBusinessEntityDetails(key));
                return forkJoin(obs).pipe(
                    map((data: any[]) => {
                        const businessEntities: Record<string, any[]> = {};
                        keys.forEach((key, i) => {
                            businessEntities[key] = data[i]?.code === 200 ? data[i].data : [];
                        });
                        return CommonActions.loadBusinessEntitiesSuccess({ businessEntities });
                    }),
                    catchError(err => of(CommonActions.loadBusinessEntitiesFailure({
                        error: err?.error?.message ?? 'Failed to load business entities'
                    })))
                );
            })
        ),
    { functional: true }
);

export const loadAllUsers$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadAllUsers),
            switchMap(() =>
                commonService.getAllUsers().pipe(
                    map((res: any) => CommonActions.loadAllUsersSuccess({ users: res.data ?? [] })),
                    catchError(err => of(CommonActions.loadAllUsersFailure({ error: err?.error?.message ?? 'Failed to load users' })))
                )
            )
        ),
    { functional: true }
);

export const loadAllInternalUsers$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadAllInternalUsers),
            switchMap(() =>
                commonService.getAllInternalUsers().pipe(
                    map((res: any) => CommonActions.loadAllInternalUsersSuccess({ users: res.data ?? [] })),
                    catchError(err => of(CommonActions.loadAllInternalUsersFailure({ error: err?.error?.message ?? 'Failed to load internal users' })))
                )
            )
        ),
    { functional: true }
);

export const loadAllRoles$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadAllRoles),
            switchMap(() =>
                commonService.getAllRoles().pipe(
                    map((res: any) => CommonActions.loadAllRolesSuccess({ roles: res.data ?? [] })),
                    catchError(err => of(CommonActions.loadAllRolesFailure({ error: err?.error?.message ?? 'Failed to load roles' })))
                )
            )
        ),
    { functional: true }
);

export const loadAllAE$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadAllAE),
            switchMap(() =>
                commonService.getAllAE().pipe(
                    map((res: any) => CommonActions.loadAllAESuccess({ ae: res.data ?? [] })),
                    catchError(err => of(CommonActions.loadAllAEFailure({ error: err?.error?.message ?? 'Failed to load AE list' })))
                )
            )
        ),
    { functional: true }
);

export const loadAllRec$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadAllRec),
            switchMap(() =>
                commonService.getAllRec().pipe(
                    map((res: any) => CommonActions.loadAllRecSuccess({ rec: res.data ?? [] })),
                    catchError(err => of(CommonActions.loadAllRecFailure({ error: err?.error?.message ?? 'Failed to load recruiters' })))
                )
            )
        ),
    { functional: true }
);

export const loadAllTeamLead$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadAllTeamLead),
            switchMap(() =>
                commonService.getAllTeamLead().pipe(
                    map((res: any) => CommonActions.loadAllTeamLeadSuccess({ leads: res.data ?? [] })),
                    catchError(err => of(CommonActions.loadAllTeamLeadFailure({ error: err?.error?.message ?? 'Failed to load team leads' })))
                )
            )
        ),
    { functional: true }
);

export const loadAllClients$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadAllClients),
            switchMap(() =>
                commonService.getAllClient().pipe(
                    map((res: any) => CommonActions.loadAllClientsSuccess({ clients: res.data ?? [] })),
                    catchError(err => of(CommonActions.loadAllClientsFailure({ error: err?.error?.message ?? 'Failed to load clients' })))
                )
            )
        ),
    { functional: true }
);

export const loadQBUsers$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadQBUsers),
            switchMap(() =>
                commonService.getAllQbUsers().pipe(
                    map((res: any) => CommonActions.loadQBUsersSuccess({ users: res.data ?? [] })),
                    catchError(err => of(CommonActions.loadQBUsersFailure({ error: err?.error?.message ?? 'Failed to load QB users' })))
                )
            )
        ),
    { functional: true }
);

export const loadAllCurrency$ = createEffect(
    (actions$ = inject(Actions), commonService = inject(CommonService)) =>
        actions$.pipe(
            ofType(CommonActions.loadAllCurrency),
            switchMap(() =>
                commonService.getAllCurrency().pipe(
                    map((res: any) => CommonActions.loadAllCurrencySuccess({ currency: res.data ?? [] })),
                    catchError(err => of(CommonActions.loadAllCurrencyFailure({ error: err?.error?.message ?? 'Failed to load currency list' })))
                )
            )
        ),
    { functional: true }
);
